/**
 * 콜게이트 예약 페이지 UI A/B 테스트 구분값
 *
 * - A: 기존 한 화면 예약 UI
 * - B: 날짜 → 인원·게임 → 시간 → 예약 확인 4단계 UI
 *
 * 나누는 기준 (앞에서부터 적용)
 * 1. `ui` 파라미터 — QA에서 화면을 강제로 볼 때만 쓴다 (예: /booking?shopId=...&ui=B)
 * 2. `caller` — ARS 링크의 암호화된 전화번호. 고정 키 AES/ECB라 같은 번호면 항상 같은 값이므로
 *    복호화 없이 해시해 반반 나눈다 → 같은 매장 안에서도 손님별로 나뉘고, 재방문해도 같은 화면이 나온다
 * 3. 둘 다 없으면 A — caller 없는 방문은 퍼널 로그가 쌓이지 않아 실험 대상이 아니다
 *
 * 페이지를 처음 연 주소(진입 링크)의 값으로 정하고, 앱 안에서 이동해 URL에서
 * 파라미터가 빠져도 같은 방문 동안은 그 값을 유지한다. 새로 열면 다시 주소 기준으로 정한다.
 */
export type UiVariant = 'A' | 'B';

/** 구분값을 정한 근거 — 로그 data.uiSource로 남겨 QA 강제 세션(PARAM)을 집계에서 뺄 수 있게 한다 */
export type UiVariantSource = 'PARAM' | 'CALLER' | 'NONE';

const toUiVariant = (value: string): UiVariant => (value.toUpperCase() === 'B' ? 'B' : 'A');

/** FNV-1a 32bit — 같은 caller면 어느 기기에서든 같은 값이 나온다 */
const hashString = (value: string): number => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
};

export function resolveUiVariant(search: string): { variant: UiVariant; source: UiVariantSource } {
  const params = new URLSearchParams(search);

  const forced = params.get('ui');
  if (forced) return { variant: toUiVariant(forced), source: 'PARAM' };

  // 암호문은 HEX라 대소문자만 다를 수 있다 — 같은 번호가 다른 그룹으로 가지 않도록 맞춘다
  const caller = params.get('caller')?.trim().toLowerCase();
  if (caller) return { variant: hashString(caller) % 2 === 0 ? 'A' : 'B', source: 'CALLER' };

  return { variant: 'A', source: 'NONE' };
}

// 모듈 로드 시점(= 앱 진입 시점)의 주소 기준 — index.tsx에서 가장 먼저 import 한다
const entry = resolveUiVariant(window.location.search);

export function getUiVariant(): UiVariant {
  // 앱 안에서 ui 파라미터가 붙은 주소로 이동한 경우엔 그 값을 우선한다 (QA용)
  const current = new URLSearchParams(window.location.search).get('ui');
  return current ? toUiVariant(current) : entry.variant;
}

export function getUiVariantSource(): UiVariantSource {
  return new URLSearchParams(window.location.search).get('ui') ? 'PARAM' : entry.source;
}
